import type {
  ArrivalTrackCondition,
  BroadcastCondition,
  BroadcastConditionOperator,
  BroadcastConditionType,
  ConnectingConditionJudgementMode,
  ConnectingLineDelayCondition,
} from '../../../api/types';
import { findPlatformStopIds, platformLabel, type GtfsStop } from '../../../data/gtfsStops';
import type { RouteMaster } from '../../../data/routes';
import { sortStopsBySequence, type RouteStop } from '../../../data/routeStops';
import { createDefaultBroadcastCondition } from '../../../utils/broadcastContents';
import { clampNonNegativeInt, NumericInput } from '../../NumericInput';
import {
  BROADCAST_CONDITION_TYPE_META,
  CONNECTING_CONDITION_MODE_META,
  OPERATOR_META,
} from '../constants';
import { StopSelect } from '../parts/StopSelect';

/**
 * 条件種別セレクト (「接続線遅延」/「停車番線」)。
 *
 * 種別を切り替えたときは patch (部分更新) ではなく **条件オブジェクトを丸ごと差し替える**。
 * `BroadcastCondition` は種別ごとに入力項目がまったく異なるため、patch でマージすると
 * 他種別のフィールド (例: 停車番線に切り替えたのに `route_id` / `delay_minutes` が残る) が
 * DB / JSON に残ってしまう。既存の判定モード切替 (`handleModeChange`) と同じ方針。
 *
 * 停車番線条件の行にもこのセレクトを表示することで、**種別を接続線遅延へ戻せる**ように
 * する (旧プレースホルダは削除しかできず、戻す手段が無かった)。
 */
function ConditionTypeSelect({
  value,
  onSelect,
}: {
  value: BroadcastConditionType;
  onSelect: (next: BroadcastConditionType) => void;
}): React.JSX.Element {
  return (
    <label className="broadcast-condition-field--type">
      <span className="required">条件種別</span>
      <select
        aria-label="条件種別"
        data-testid="broadcast-condition-type"
        value={value}
        onChange={(e) => onSelect(e.target.value as BroadcastConditionType)}
      >
        {BROADCAST_CONDITION_TYPE_META.map((m) => (
          <option key={m.type} value={m.type}>
            {m.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * 条件種別を切り替えたときの次の条件オブジェクトを生成する。
 *
 * patch ではなく完成済みオブジェクトを返す (= 他種別のフィールドを一切引き継がない)。
 * - `arrival_track` → 停車番線の初期値のみを持つ条件。
 * - `connecting_line_delay` → `createDefaultBroadcastCondition()` の戻り値そのまま。
 */
function createConditionForType(nextType: BroadcastConditionType): BroadcastCondition {
  if (nextType === 'arrival_track') {
    return {
      condition_type: 'arrival_track',
      arrival_stop_id: '',
    };
  }
  return createDefaultBroadcastCondition();
}

/**
 * 1 条件行のエディタ。ConnectingLineDelayFields のコンパクト版として振る舞う。
 *
 * 判定モードの切替時には、他モードでしか使わない入力項目 (区間駅・乗換駅・乗換時間・方面) を
 * 明示的にクリアして DB / JSON にゴミが残らないようにする。
 *
 * `condition_type === 'arrival_track'` (停車番線条件) は入力項目がまったく異なるため、
 * 判別可能ユニオンの早期分岐で専用行 ({@link ArrivalTrackConditionRow}) を描画する。
 */
export function BroadcastConditionRow({
  condition,
  routes,
  routeStops,
  gtfsStops = [],
  parentRouteId,
  onChange,
  onReplace,
  onRemove,
}: {
  condition: BroadcastCondition;
  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  /**
   * 停車番線条件の番線候補ソース (GTFS 静的 stops の全件)。
   * `route_id` で絞り込めないため親ページが 1 回だけ全件取得して流す。
   * 空配列 (取得失敗) のときは番線セレクトを無効化する。
   */
  gtfsStops?: GtfsStop[];
  /**
   * 停車駅ドロップダウンを自路線に絞り込むための route_id。
   * `routeStops` の先頭キーの暗黙採用は禁止 (意図しない路線の駅が出るため)。
   */
  parentRouteId?: string;
  onChange: (patch: Partial<ConnectingLineDelayCondition>) => void;
  onReplace: (next: BroadcastCondition) => void;
  onRemove: () => void;
}): React.JSX.Element {
  if (condition.condition_type === 'arrival_track') {
    return (
      <ArrivalTrackConditionRow
        condition={condition}
        routeStops={routeStops}
        gtfsStops={gtfsStops}
        parentRouteId={parentRouteId}
        onReplace={onReplace}
        onRemove={onRemove}
      />
    );
  }
  const currentRouteId = condition.route_id ?? '';

  const mode: ConnectingConditionJudgementMode = condition.delay_judgement_mode ?? 'whole_line';
  const operator: BroadcastConditionOperator = condition.operator ?? 'gte';
  const delayMinutes =
    typeof condition.delay_minutes === 'number' && !Number.isNaN(condition.delay_minutes)
      ? condition.delay_minutes
      : 0;

  const isWholeLine = mode === 'whole_line';
  const showSectionFields = mode === 'section_scheduled';
  const showTransferFields = mode === 'transfer_aware';
  const showDirectionField = !isWholeLine;

  // 選択中の接続線 (route_id) に紐づく駅一覧。stop_sequence 昇順に整列する。
  const stops = sortStopsBySequence(currentRouteId ? routeStops[currentRouteId] : []);

  // マスタに無い route_id (古いデータ・別線区参照など) も選択肢に残す。
  const isUnknownRoute =
    currentRouteId !== '' && !routes.some((r) => r.route_id === currentRouteId);

  const currentRoute = routes.find((r) => r.route_id === currentRouteId);
  const directionOptions = (currentRoute?.directions ?? []).map((d, i) => ({
    value: typeof d.direction_id === 'string' && d.direction_id !== '' ? d.direction_id : String(i),
    label: d.direction_name,
  }));
  const directionId = condition.direction_id ?? '';
  const isUnknownDirection =
    directionId !== '' && !directionOptions.some((o) => o.value === directionId);

  const transferTimeSeconds =
    typeof condition.transfer_time_seconds === 'number' &&
    !Number.isNaN(condition.transfer_time_seconds)
      ? condition.transfer_time_seconds
      : 180;

  // 判定モード切替: 不要フィールドを明示的にクリアする。
  const handleModeChange = (nextMode: ConnectingConditionJudgementMode): void => {
    const next: BroadcastCondition = {
      route_id: condition.route_id,
      delay_judgement_mode: nextMode,
      delay_minutes: condition.delay_minutes,
      operator: condition.operator,
    };
    if (nextMode === 'section_scheduled') {
      next.start_stop_id = condition.start_stop_id;
      next.end_stop_id = condition.end_stop_id;
      next.direction_id = condition.direction_id;
    } else if (nextMode === 'transfer_aware') {
      next.transfer_stop_id = condition.transfer_stop_id;
      next.transfer_time_seconds =
        typeof condition.transfer_time_seconds === 'number' ? condition.transfer_time_seconds : 180;
      next.direction_id = condition.direction_id;
    }
    // whole_line のときは start/end_stop_id / transfer_* / direction_id を落とす
    onReplace(next);
  };

  const handleRouteChange = (nextRouteId: string): void => {
    // 路線を変えたら別路線の stop_id / direction_id が残らないようにクリアする。
    onReplace({
      route_id: nextRouteId,
      delay_judgement_mode: mode,
      delay_minutes: delayMinutes,
      operator,
      transfer_time_seconds: showTransferFields ? transferTimeSeconds : undefined,
    });
  };

  return (
    <div className="broadcast-condition-fields">
      <div className="broadcast-condition-fields__header">
        <strong>接続線遅延の条件</strong>
        <button
          type="button"
          onClick={onRemove}
          data-testid="broadcast-condition-remove"
          aria-label="この条件を削除"
        >
          × 削除
        </button>
      </div>

      <ConditionTypeSelect
        value="connecting_line_delay"
        onSelect={(nextType) => {
          if (nextType === 'connecting_line_delay') return;
          onReplace(createConditionForType(nextType));
        }}
      />

      <label className="broadcast-condition-field--route">
        <span className="required">接続線 route_id</span>

        <select
          aria-label="接続線 route_id"
          value={currentRouteId}
          onChange={(e) => handleRouteChange(e.target.value)}
        >
          <option value="">(選択してください)</option>
          {routes.map((r) => (
            <option key={r.route_id} value={r.route_id}>
              {r.route_id}
            </option>
          ))}
          {isUnknownRoute ? (
            <option value={currentRouteId}>{currentRouteId} (一覧にない)</option>
          ) : null}
        </select>
      </label>

      <label className="broadcast-condition-field--mode">
        <span className="required">判定モード</span>
        <select
          aria-label="判定モード"
          value={mode}
          onChange={(e) => handleModeChange(e.target.value as ConnectingConditionJudgementMode)}
        >
          {CONNECTING_CONDITION_MODE_META.map((m) => (
            <option key={m.mode} value={m.mode}>
              {m.label}
            </option>
          ))}
        </select>
      </label>

      {showSectionFields ? (
        <>
          <label className="broadcast-condition-field--stop">
            <span className="required">遅延判定開始駅</span>
            <select
              aria-label="遅延判定開始駅"
              value={condition.start_stop_id ?? ''}
              onChange={(e) => onChange({ start_stop_id: e.target.value })}
            >
              <option value="">(選択してください)</option>
              {stops.map((s) => (
                <option key={s.stop_id} value={s.stop_id}>
                  {s.stop_name || s.stop_id}
                </option>
              ))}
            </select>
          </label>
          <label className="broadcast-condition-field--stop">
            <span className="required">遅延判定終了駅</span>
            <select
              aria-label="遅延判定終了駅"
              value={condition.end_stop_id ?? ''}
              onChange={(e) => onChange({ end_stop_id: e.target.value })}
            >
              <option value="">(選択してください)</option>
              {stops.map((s) => (
                <option key={s.stop_id} value={s.stop_id}>
                  {s.stop_name || s.stop_id}
                </option>
              ))}
            </select>
          </label>
        </>
      ) : null}

      {showTransferFields ? (
        <>
          <label className="broadcast-condition-field--stop">
            <span className="required">乗換駅</span>
            <select
              aria-label="乗換駅"
              value={condition.transfer_stop_id ?? ''}
              onChange={(e) => onChange({ transfer_stop_id: e.target.value })}
            >
              <option value="">(選択してください)</option>
              {stops.map((s) => (
                <option key={s.stop_id} value={s.stop_id}>
                  {s.stop_name || s.stop_id}
                </option>
              ))}
            </select>
          </label>
          <label className="broadcast-condition-field--number">
            <span className="required">乗換時間 (秒)</span>
            <NumericInput
              min={0}
              step={1}
              aria-label="乗換時間 (秒)"
              value={transferTimeSeconds}
              normalizeValue={clampNonNegativeInt}
              onChange={(nextTransferTime) => onChange({ transfer_time_seconds: nextTransferTime })}
            />
          </label>
        </>
      ) : null}

      {showDirectionField ? (
        <label className="broadcast-condition-field--direction">
          <span>方面 (任意)</span>
          <select
            aria-label="方面"
            value={directionId}
            onChange={(e) => onChange({ direction_id: e.target.value })}
          >
            <option value="">(指定なし)</option>
            {directionOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
            {isUnknownDirection ? (
              <option value={directionId}>{directionId} (一覧にない)</option>
            ) : null}
          </select>
        </label>
      ) : null}

      <>
        <label className="broadcast-condition-field--number">
          <span className="required">遅延分数</span>
          <NumericInput
            min={0}
            step={1}
            aria-label="遅延分数"
            value={delayMinutes}
            normalizeValue={clampNonNegativeInt}
            onChange={(nextDelayMinutes) => onChange({ delay_minutes: nextDelayMinutes })}
          />
        </label>
        <label className="broadcast-condition-field--operator">
          <span className="required">比較</span>
          <select
            aria-label="比較演算子"
            value={operator}
            onChange={(e) => onChange({ operator: e.target.value as BroadcastConditionOperator })}
          >
            {OPERATOR_META.map((o) => (
              <option key={o.operator} value={o.operator}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <span className="broadcast-condition-fields__summary">
          (遅延 {operator === 'gte' ? '≧' : '≦'} {delayMinutes} 分のとき放送)
        </span>
      </>
    </div>
  );
}

/**
 * `arrival_stop_id` (子 stop_id) から「選択中の停車駅」(親 stop_id) を導出する。
 *
 * 停車番線条件が保持するのは子 stop_id 1 項目のみ (型変更禁止) なので、
 * 駅を別フィールドで持たず **値から導出**する。
 * - `"南浦和:上中"` → `"南浦和"` (`":"` の前を親駅として扱う)
 * - `":"` を含まない値 (番線分割の無い駅) → その値自身が駅
 * - 空文字 → 駅も未選択
 *
 * 番線名に `":"` は含まれない (build-gtfs が `<駅名>:<番線>` 形式で生成する) ため、
 * 最初の `":"` での分割で十分。
 */
function deriveStationStopId(arrivalStopId: string): string {
  if (arrivalStopId === '') return '';
  const separatorIndex = arrivalStopId.indexOf(':');
  return separatorIndex === -1 ? arrivalStopId : arrivalStopId.slice(0, separatorIndex);
}

/**
 * 停車番線条件 (`condition_type: 'arrival_track'`) の入力 UI。
 *
 * 「停車駅を選ぶ → 番線を選ぶ」の 2 段ドロップダウンで設定し、**保存値は GTFS 子 stop_id
 * 1 項目**(例 `"南浦和:上中"`)。駅名と番線名を別フィールドに持たないため、駅・番線の
 * 整合バリデーションや正規化は不要 (`api/types.ts` の `ArrivalTrackCondition` 参照)。
 *
 * - 停車駅の候補は自路線 (`parentRouteId`) の駅一覧。`routeStops` の先頭キーは
 *   暗黙採用しない (意図しない路線の駅が出る不具合の原因だったため)。
 * - 番線の候補は `findPlatformStopIds(gtfsStops, 駅)` (親駅一致の子 stop_id)。
 *   ラベルは `platformLabel` で番線名のみを表示するが、**value は子 stop_id そのまま**。
 * - 駅・番線の変更はいずれも `onReplace` で条件オブジェクトを丸ごと差し替える
 *   (patch の二重キャストを避け、他種別のフィールドが混ざらないようにする)。
 */
function ArrivalTrackConditionRow({
  condition,
  routeStops,
  gtfsStops,
  parentRouteId,
  onReplace,
  onRemove,
}: {
  condition: ArrivalTrackCondition;
  routeStops: Record<string, RouteStop[]>;
  gtfsStops: GtfsStop[];
  parentRouteId?: string;
  onReplace: (next: BroadcastCondition) => void;
  onRemove: () => void;
}): React.JSX.Element {
  const arrivalStopId = condition.arrival_stop_id ?? '';
  const stationStopId = deriveStationStopId(arrivalStopId);

  // 停車駅の候補は自路線の駅一覧のみ (parentRouteId 未指定なら空 = (指定なし) のみ)。
  const stationOptions = sortStopsBySequence(parentRouteId ? routeStops[parentRouteId] : []);

  // 番線候補の取得可否。gtfsStops が空 (取得失敗 / 未取得) のときは番線セレクトを無効化する。
  const hasGtfsStops = gtfsStops.length > 0;
  // 駅未選択のあいだは番線を選べない (親駅が決まらないと候補を絞れない)。
  const platformStopIds =
    stationStopId !== '' && hasGtfsStops ? findPlatformStopIds(gtfsStops, stationStopId) : [];
  // findPlatformStopIds は子 stop が無い駅では [駅 ID] を返す = 番線分割の無い駅。
  const hasNoPlatformSplit = platformStopIds.length === 1 && platformStopIds[0] === stationStopId;

  // 「駅だけ選んだ直後」の表示。
  //
  // 保存するのは子 stop_id 1 項目だけなので、駅を選んだ時点では `arrival_stop_id` に
  // 親駅 ID が入る (駅の状態はこの値から導出する / 型は変更しない方針)。
  // 番線が分かれている駅ではこの親駅 ID は「番線未選択」を意味するため、
  // 番線セレクトは「(指定なし)」表示にする (候補外の値として扱わない)。
  // 逆に番線分割の無い駅では親駅 ID 自体が正しい保存値で候補にも含まれるため、
  // そのまま選択済みとして表示される。
  const isPlatformUnselected =
    arrivalStopId === stationStopId && !platformStopIds.includes(arrivalStopId);
  const platformSelectValue = isPlatformUnselected ? '' : arrivalStopId;
  // 既存データの保全: 候補に無い値 (別日付の GTFS / 廃止番線 / 未取得) も選択肢に残す。
  const isUnknownPlatform =
    platformSelectValue !== '' && !platformStopIds.includes(platformSelectValue);

  const handleStationChange = (nextStationStopId: string): void => {
    // 駅を変えたら別駅の番線が残らないよう arrival_stop_id をクリアする
    // (接続線遅延の handleRouteChange と同方針)。
    onReplace({
      condition_type: 'arrival_track',
      arrival_stop_id: nextStationStopId === '' ? '' : nextStationStopId,
    });
  };

  const handlePlatformChange = (nextArrivalStopId: string): void => {
    onReplace({
      condition_type: 'arrival_track',
      arrival_stop_id: nextArrivalStopId,
    });
  };

  return (
    <div className="broadcast-condition-fields">
      <div className="broadcast-condition-fields__header">
        <strong>停車番線の条件</strong>
        <button
          type="button"
          onClick={onRemove}
          data-testid="broadcast-condition-remove"
          aria-label="この条件を削除"
        >
          × 削除
        </button>
      </div>

      <ConditionTypeSelect
        value="arrival_track"
        onSelect={(nextType) => {
          if (nextType === 'arrival_track') return;
          onReplace(createConditionForType(nextType));
        }}
      />

      <p className="broadcast-condition-fields__note">
        指定した番線に停車するかどうかで判定します。終着・途中停車のどちらでも成立します
        （通過する場合は成立しません）。
      </p>

      <>
        <label className="broadcast-condition-field--stop">
          <span className="required">停車駅</span>
          <StopSelect
            ariaLabel="停車駅"
            testId="broadcast-condition-arrival-station"
            value={stationStopId}
            stops={stationOptions}
            onChange={handleStationChange}
          />
        </label>
        <label className="broadcast-condition-field--platform">
          <span className="required">停車番線 (stop_id)</span>
          <select
            aria-label="停車番線"
            data-testid="broadcast-condition-arrival-track"
            value={platformSelectValue}
            disabled={stationStopId === '' || !hasGtfsStops}
            onChange={(e) => handlePlatformChange(e.target.value)}
          >
            <option value="">(指定なし)</option>
            {platformStopIds.map((stopId) => (
              <option key={stopId} value={stopId}>
                {platformLabel(stopId, stationStopId)}
              </option>
            ))}
            {isUnknownPlatform ? (
              <option value={arrivalStopId}>{arrivalStopId} (候補未取得)</option>
            ) : null}
          </select>
        </label>
      </>

      {!hasGtfsStops ? (
        <p
          className="broadcast-condition-fields__note broadcast-condition-fields__note--warn"
          data-testid="broadcast-condition-arrival-track-unavailable"
        >
          番線候補を取得できませんでした。時間をおいて再読み込みしてください
          （設定済みの停車番線はそのまま保持されます）。
        </p>
      ) : hasNoPlatformSplit ? (
        <p
          className="broadcast-condition-fields__note"
          data-testid="broadcast-condition-arrival-track-no-split"
        >
          この駅は番線が分かれていません。駅 ID がそのまま停車番線として保存されます。
        </p>
      ) : null}
    </div>
  );
}
